import React, { useState, useEffect } from 'react';
import { User, Search, UserCheck, X, AlertTriangle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/apiService';

const TransferOwnershipModal = ({ isOpen, onClose, list, onTransfer }) => {
  const { t } = useLanguage();
  const { user: currentUser } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isTransferring, setIsTransferring] = useState(false);
  const [error, setError] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  const isAdmin = currentUser?.isAdmin;

  useEffect(() => {
    if (!isOpen) {
      // Reset state when modal closes
      setSearchTerm('');
      setSearchResults([]);
      setSelectedUser(null);
      setError('');
      setHasSearched(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const performSearch = async () => {
      if (searchTerm.length === 0) {
        setSearchResults([]);
        setHasSearched(false);
        setSelectedUser(null);
        return;
      }
      
      // For non-admin users, we need a complete email address
      if (!isAdmin && searchTerm.length < 3) {
        return;
      }

      // For admin users, require at least 2 characters for search
      if (isAdmin && searchTerm.length < 2) {
        return;
      }

      setIsSearching(true);
      setHasSearched(false);
      setError('');
      
      try {
        let results = [];
        
        if (isAdmin) {
          // Admin can search by partial name/email
          results = await apiService.searchUsers(searchTerm);
        } else {
          // Regular users must provide exact email and we'll try to find the user
          // Only attempt search if it looks like a complete email
          if (searchTerm.includes('@') && searchTerm.includes('.')) {
            try {
              const user = await apiService.findUserByEmail(searchTerm);
              results = [user];
            } catch (err) {
              // User not found - this is expected, not an error to display
              results = [];
            }
          }
        }
        
        // Filter out the current owner
        const filteredResults = results.filter(user => user.id !== list?.userId);
        
        setSearchResults(filteredResults);
        setHasSearched(true);

        // Auto-select if exactly one result for non-admin users
        if (!isAdmin && filteredResults.length === 1) {
          setSelectedUser(filteredResults[0]);
        }
      } catch (err) {
        console.error('Failed to search users:', err);
        if (err.message.includes('Access denied')) {
          setError(t('adminSearchOnly', 'Only administrators can search for users. Please enter the complete email address.'));
        } else {
          setError(t('failedToSearchUsers', 'Failed to search users'));
        }
        setHasSearched(true);
      } finally {
        setIsSearching(false);
      }
    };

    const timeoutId = setTimeout(performSearch, isAdmin ? 100 : 300); // Longer delay for email validation
    return () => clearTimeout(timeoutId);
  }, [searchTerm, list?.userId, t, isAdmin]);

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setSearchTerm(user.email);
    setSearchResults([]);
  };

  const handleTransfer = async () => {
    if (!selectedUser || !list) {
      setError(t('pleaseSelectUser', 'Please select a user'));
      return;
    }

    setIsTransferring(true);
    setError('');

    try {
      await apiService.transferListOwnership(list.id, selectedUser.id);
      
      if (onTransfer) {
        onTransfer(list.id, selectedUser);
      }
      
      onClose();
    } catch (err) {
      console.error('Failed to transfer ownership:', err);
      setError(err.message || t('failedToTransferOwnership', 'Failed to transfer ownership'));
    } finally {
      setIsTransferring(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose} onKeyDown={handleKeyDown} tabIndex={-1}>
      <div className="modal-content transfer-ownership-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">
            <UserCheck size={24} />
            <h3>{t('transferOwnership', 'Transfer Ownership')}</h3>
          </div>
          <button onClick={onClose} className="modal-close-btn">
            <X size={20} />
          </button>
        </div>

        <div className="modal-body">
          <div className="transfer-info">
            <div className="current-list-info">
              <h4>{t('listToTransfer', 'List to transfer')}: <strong>{list?.name}</strong></h4>
            </div>

            <div className="warning-section compact">
              <div className="warning-card compact">
                <AlertTriangle size={16} />
                <div>
                  <p>{t('transferOwnershipWarning', 'Once you transfer ownership, you will no longer be the owner of this list.')}</p>
                </div>
              </div>
            </div>

            <div className="user-search-section">
              <h4>{t('selectNewOwner', 'Select New Owner')}</h4>
              <div className="search-instructions">
                {isAdmin ? (
                  <p style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
                    {t('adminSearchInstruction', 'As an administrator, you can search by name or email to find users.')}
                  </p>
                ) : (
                  <p style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
                    {t('userEmailInstruction', 'Enter the complete email address of the person you want to transfer ownership to.')}
                  </p>
                )}
              </div>
              <div className="search-container">
                <div className="input-with-icon">
                  <Search size={18} />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={isAdmin 
                      ? t('searchByEmail', 'Search by name or email address...') 
                      : t('enterCompleteEmail', 'Enter complete email address...')
                    }
                    className="user-search-input"
                    disabled={isTransferring}
                  />
                </div>
              </div>

              {isSearching && (
                <div className="search-loading">
                  <div className="loading-spinner"></div>
                  <span>{t('searchingUsers', 'Searching users...')}</span>
                </div>
              )}
              
              {searchResults.length > 0 && (
                <div style={{
                  border: '1px solid #ccc',
                  background: '#f8f9fa',
                  padding: '10px',
                  margin: '10px 0',
                  borderRadius: '8px'
                }}>
                  <h5>{t('searchResults', 'Search Results')}</h5>
                  {searchResults.map(user => (
                    <div
                      key={user.id}
                      onClick={() => handleUserSelect(user)}
                      style={{
                        border: '1px solid #ddd',
                        margin: '5px 0',
                        padding: '10px',
                        cursor: 'pointer',
                        backgroundColor: selectedUser?.id === user.id ? '#e3f2fd' : 'white',
                        borderRadius: '4px'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <User size={16} />
                        <div>
                          <div style={{ fontWeight: 'bold' }}>{user.name}</div>
                          <div style={{ fontSize: '0.9em', color: '#666' }}>{user.email}</div>
                        </div>
                        {selectedUser?.id === user.id && (
                          <UserCheck size={16} style={{ marginLeft: 'auto', color: '#1976d2' }} />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {searchTerm.length >= 2 && !isSearching && hasSearched && searchResults.length === 0 && (
                <div className="no-results">
                  {t('noUsersFound', 'No users found with that email')}
                </div>
              )}
            </div>

            {selectedUser && (
              <div className="selected-user-preview compact">
                <h4>{t('newOwnerPreview', 'New Owner')}: {selectedUser.name} ({selectedUser.email})</h4>
              </div>
            )}

            {error && (
              <div className="error-message">
                <AlertTriangle size={16} />
                <span>{error}</span>
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="btn-secondary" disabled={isTransferring}>
            {t('cancel', 'Cancel')}
          </button>
          <button
            onClick={handleTransfer}
            className="btn-primary"
            disabled={!selectedUser || isTransferring}
          >
            {isTransferring ? (
              <>
                <div className="loading-spinner"></div>
                {t('transferring', 'Transferring...')}
              </>
            ) : (
              <>
                <UserCheck size={16} />
                {t('transferOwnership', 'Transfer Ownership')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransferOwnershipModal;
